import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Business, Perk } from "@dropin/shared/schemas";
import {
  type AdminBusiness,
  type AdminBusinessStats,
  adminCreateBusinessPerk,
  adminGetBusiness,
  adminListBusinessPerks,
  adminUpdateBusinessPerk,
  updateBusinessPlan,
} from "../api";
import { BusinessProfileTab } from "../components/BusinessProfileTab";
import { PerkForm } from "../components/PerkForm";
import { Toggle } from "../components/Toggle";

export function AdminBusinessDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [biz, setBiz] = useState<AdminBusiness | null>(null);
  const [stats, setStats] = useState<AdminBusinessStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planBusy, setPlanBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { business, stats } = await adminGetBusiness(id);
      setBiz(business);
      setStats(stats);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function togglePlan() {
    if (!biz) return;
    const next: "free" | "pro" = biz.plan === "pro" ? "free" : "pro";
    setPlanBusy(true);
    try {
      await updateBusinessPlan(biz.id, next);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "plan change failed");
    } finally {
      setPlanBusy(false);
    }
  }

  if (loading) return <div className="card text-muted">Loading…</div>;
  if (error && !biz) {
    return (
      <div className="card max-w-md">
        <h1 className="text-xl font-semibold tracking-tightish">Couldn't load business</h1>
        <p className="mt-2 text-sm text-rose">{error}</p>
        <button className="btn mt-4" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    );
  }
  if (!biz) return null;

  const initial = (biz.name?.[0] ?? "?").toUpperCase();
  const locationLine = [biz.address, biz.city].filter(Boolean).join(" · ");

  return (
    <div className="grid gap-5 pb-28 md:pb-0">
      <Link
        to="/dashboard"
        className="text-sm text-muted hover:text-ink inline-flex items-center gap-1 self-start"
      >
        ← Back to businesses
      </Link>

      {/* Identity header */}
      <section className="card">
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-20 w-20 rounded-lg border border-border bg-paper overflow-hidden flex items-center justify-center">
            {biz.logoUrl ? (
              <img
                src={biz.logoUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span className="text-3xl font-semibold text-muted" aria-hidden>
                {initial}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {biz.plan === "pro" ? (
                <span className="chip-success text-xs">Pro</span>
              ) : (
                <span className="chip text-xs">Free</span>
              )}
              {biz.lat == null && (
                <span className="chip text-xs text-muted">ungeocoded</span>
              )}
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tightish truncate">{biz.name}</h1>
            {locationLine && <p className="text-sm text-muted truncate">{locationLine}</p>}
            {biz.ownerEmail && (
              <p className="mt-0.5 text-xs text-muted truncate">Owner: {biz.ownerEmail}</p>
            )}
            <p className="mt-0.5 text-xs text-muted">
              Joined {new Date(biz.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {stats && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <Stat label="Perks" value={stats.totalPerks} />
            <Stat label="Active perks" value={stats.activePerks} />
            <Stat label="Total claims" value={stats.totalClaims} />
            <Stat
              label="Brief"
              valueText={
                stats.briefStatus === "published"
                  ? "Published"
                  : stats.briefStatus === "draft"
                    ? "Draft"
                    : "—"
              }
              // Published briefs have a public URL — drafts don't, so only
              // the published state becomes a link.
              href={
                stats.briefStatus === "published"
                  ? `/brief/${biz.shortCode || biz.id}`
                  : undefined
              }
              testid="admin-stat-brief"
            />
          </div>
        )}
      </section>

      {/* Plan management */}
      <section className="card">
        <h3 className="text-lg font-semibold tracking-tightish">Plan</h3>
        <p className="mt-1 text-sm text-muted">
          Pro businesses can post unlimited perks and accept claims. Free is brief-only.
        </p>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-ink">
            Currently: <strong>{biz.plan === "pro" ? "Pro" : "Free"}</strong>
          </span>
          <button
            type="button"
            className={biz.plan === "pro" ? "btn text-sm" : "btn-primary text-sm"}
            onClick={togglePlan}
            disabled={planBusy}
            data-testid="admin-plan-toggle"
          >
            {planBusy
              ? "…"
              : biz.plan === "pro"
                ? "Move to Free"
                : "Upgrade to Pro"}
          </button>
        </div>
      </section>

      {error && <div className="card text-sm text-rose">{error}</div>}

      {/* Perks — admin can create + edit on behalf of the business,
          bypassing the Free-plan limit. */}
      <AdminPerksSection businessId={biz.id} onPerksChanged={refresh} />

      {/* Full profile editor — admin uses the same PATCH /businesses/:id
          path; the API allows owner or admin. adminMode also routes the
          embedded brief editor through /admin/businesses/:id/brief. */}
      <BusinessProfileTab
        business={biz as Business}
        adminMode
        onUpdated={(b) => setBiz({ ...biz, ...b })}
      />
    </div>
  );
}

function AdminPerksSection({
  businessId,
  onPerksChanged,
}: {
  businessId: string;
  // Lets the parent refresh the business detail (stats include perk counts)
  // after a create/edit so the header tiles stay in sync.
  onPerksChanged: () => void;
}) {
  const [perks, setPerks] = useState<Perk[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { perks } = await adminListBusinessPerks(businessId);
      setPerks(perks);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load perks");
    }
  }, [businessId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="card grid gap-4" data-testid="admin-perks-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold tracking-tightish">Perks</h3>
          <p className="mt-1 text-sm text-muted">
            Manage perks on behalf of this business. Plan limits don't apply here.
          </p>
        </div>
        {!creating && (
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => setCreating(true)}
            data-testid="admin-perk-new"
          >
            New perk
          </button>
        )}
      </div>

      {creating && (
        <div className="rounded-md border border-border bg-paper p-4">
          <PerkForm
            businessId={businessId}
            testidPrefix="admin-perk-create"
            onCancel={() => setCreating(false)}
            onSubmit={async (values) => {
              await adminCreateBusinessPerk(businessId, {
                title: values.title,
                description: values.description,
                imageUrl: values.imageUrl ?? undefined,
                redemptionCode: values.redemptionCode ?? undefined,
                requireApproval: values.requireApproval,
              });
              setCreating(false);
              await load();
              onPerksChanged();
            }}
          />
        </div>
      )}

      {error && <p className="text-sm text-rose">{error}</p>}

      {perks === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : perks.length === 0 ? (
        <p className="text-sm text-muted">No perks yet.</p>
      ) : (
        <div className="grid gap-3">
          {perks.map((p) => (
            <AdminPerkRow
              key={p.id}
              perk={p}
              businessId={businessId}
              onChanged={async () => {
                await load();
                onPerksChanged();
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function AdminPerkRow({
  perk,
  businessId,
  onChanged,
}: {
  perk: Perk;
  businessId: string;
  onChanged: () => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <div
      className="rounded-md border border-border bg-paper p-3"
      data-testid={`admin-perk-card-${perk.id}`}
    >
      {editing ? (
        <PerkForm
          businessId={businessId}
          initial={perk}
          testidPrefix={`admin-perk-edit-${perk.id}`}
          onCancel={() => setEditing(false)}
          onSubmit={async (values) => {
            await adminUpdateBusinessPerk(businessId, perk.id, values);
            setEditing(false);
            await onChanged();
          }}
        />
      ) : (
        <div className="flex items-start gap-3">
          {perk.imageUrl && (
            <div className="shrink-0 h-14 w-14 rounded-md border border-border bg-surface overflow-hidden">
              <img
                src={perk.imageUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-base font-semibold">{perk.title}</h4>
              {!perk.active && <span className="chip text-xs">Hidden</span>}
              {perk.requireApproval && (
                <span className="chip-accent text-xs">Approval required</span>
              )}
            </div>
            {perk.description && (
              <p className="mt-1 text-sm text-muted line-clamp-2">{perk.description}</p>
            )}
            {perk.redemptionCode && (
              <p className="mt-2 text-xs text-muted">
                Code:{" "}
                <span className="font-mono uppercase tracking-wider text-ink bg-surface px-1.5 py-0.5 rounded border border-border">
                  {perk.redemptionCode}
                </span>
              </p>
            )}
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setEditing(true)}
                data-testid={`admin-perk-edit-trigger-${perk.id}`}
              >
                Edit
              </button>
            </div>
          </div>
          <Toggle
            active={perk.active}
            onChange={async (next) => {
              await adminUpdateBusinessPerk(businessId, perk.id, { active: next });
              await onChanged();
            }}
            labelOn="Active"
            labelOff="Hidden"
            testid={`admin-perk-toggle-${perk.id}`}
          />
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  valueText,
  href,
  testid,
}: {
  label: string;
  value?: number;
  valueText?: string;
  // When present, the tile becomes a clickable link to that route. Opens
  // in a new tab so the admin doesn't lose their place on the detail page.
  href?: string;
  testid?: string;
}) {
  const valueNode = (
    <p className="text-2xl font-semibold tracking-tight no-underline">
      {valueText ?? value ?? "—"}
    </p>
  );
  const labelNode = (
    <p className="text-xs text-muted uppercase tracking-wider">{label}</p>
  );
  if (href) {
    return (
      <Link
        to={href}
        target="_blank"
        rel="noreferrer"
        className="rounded-md border border-border bg-paper px-3 py-2 hover:border-primary/50 hover:bg-primary/[0.04] transition-colors no-underline text-ink"
        data-testid={testid}
      >
        {valueNode}
        {labelNode}
      </Link>
    );
  }
  return (
    <div className="rounded-md border border-border bg-paper px-3 py-2" data-testid={testid}>
      {valueNode}
      {labelNode}
    </div>
  );
}
